import { useEffect, useState } from 'react'
import { useLocation } from '@tanstack/react-router'
import { Menu } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet'
import { Sidebar } from '@/components/sidebar'
import { cn } from '@/lib/utils'

/**
 * Mobile navigation: a hamburger in the navbar that opens the app sidebar as
 * a left drawer. Closes itself on any navigation, search-only ones included. Hidden from `md` up, where the
 * sidebar rail is rendered inline instead.
 */
export function MobileNav({ className }: { className?: string }) {
  const [open, setOpen] = useState(false)
  // href, not pathname: the settings gear only adds `?settings=menu` (board h39z).
  const href = useLocation({ select: (location) => location.href })
  const { t } = useTranslation()

  useEffect(() => {
    setOpen(false)
  }, [href])

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <button
          type="button"
          aria-label={t('mobileNav.openMenu')}
          className={cn(
            'text-foreground/70 hover:bg-foreground/[0.06] hover:text-foreground flex size-9 items-center justify-center rounded-lg transition-colors md:hidden',
            className
          )}
        >
          <Menu className="size-5" />
        </button>
      </SheetTrigger>
      <SheetContent side="left" className="w-72 gap-0 p-0 sm:max-w-72" showCloseButton={false}>
        <SheetTitle className="sr-only">{t('mobileNav.menu')}</SheetTitle>
        <SheetDescription className="sr-only">{t('mobileNav.description')}</SheetDescription>
        <Sidebar variant="drawer" className="h-full w-full" />
      </SheetContent>
    </Sheet>
  )
}
